<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		FlexibleCard,
		Label,
		LiveIndicator,
		PlainTable,
		SystemStyles,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { getSystemStyle } from 'src/helpers/statistics/ui/chart_colors_helpers';
	//Helpers
	import { getEvWorkingState } from 'src/helpers/xt1_helpers/ev_charger/ev_charger_helpers';
	import { getXT1TemplateStatus } from 'src/helpers/xt1_helpers/template_status_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;

	$: statuses = getXT1TemplateStatus(device.id);
	$: workState = statuses && getEvWorkingState(statuses.find((i) => i?.role === 'work_state')?.event);
	$: liveSession = /^(charging|paused)$/.test(workState?.state);
</script>

{#if liveSession}
	<div class="lable-list-wrapper-in-drawer-tab-content">
		<Label
			size="m"
			icon={workState?.state === 'charging' ? 'fas fa-battery-bolt' : 'fas fa-pause'}
			iconColor={getSystemStyle(SystemStyles.COLOR_SUCCESS)}
			value={$format(`label_${workState?.state}`, {
				default: `${workState?.state}`,
			})}
			class="mb-m"
		/>
	</div>
{/if}
<div class="d-flex align-items-center mb-s{liveSession ? '' : ' mt-s'}">
	<Text class="text-bold mr-xs" styleType="h2"
		>{$format('label_real_time_data', {
			default: 'Real-time data',
		})}</Text
	>
	{#if liveSession}
		<LiveIndicator />
	{/if}
</div>
{#if liveSession}
	<div class="grid mb-s">
		<FlexibleCard
			backgroundColor={$showNewLayout ? '--color-0' : undefined}
			padding="m"
			class="d-block"
			mainContentPrioritySlot="both"
			header={$format('current', { default: 'Current' })}
			icon="far fa-circle-bolt"
		>
			{statuses.find((i) => i.name == 'total_current')?.value || 'N/A'}
		</FlexibleCard>
		<FlexibleCard
			backgroundColor={$showNewLayout ? '--color-0' : undefined}
			padding="m"
			class="d-block"
			mainContentPrioritySlot="both"
			header={$format('label_charging_power', {
				default: 'Charging power',
			})}
			icon="far fa-bolt"
		>
			{statuses.find((i) => i.name == 'total_power')?.value || 'N/A'}
		</FlexibleCard>
	</div>
	<FlexibleCard
		backgroundColor={$showNewLayout ? '--color-0' : undefined}
		padding="m"
		class="d-block mb-s"
		mainContentPrioritySlot="both"
		header={$format('label_energy_delivered', { default: 'Energy delivered' })}
		icon="fas fa-gauge-circle-bolt"
	>
		{statuses.find((i) => i.name == 'energy_charge')?.value || 'N/A'}
	</FlexibleCard>
	<FlexibleCard
		backgroundColor={$showNewLayout ? '--color-0' : undefined}
		class="mb-m"
		padding="m"
		mainContentPrioritySlot="both"
	>
		<PlainTable class="width-full">
			<thead>
				<tr>
					<th></th>
					<th class="text-color-muted text-600 text-uppercase"
						>{$format(`power`, {
							default: 'Power',
						})}</th
					>
					<th class="text-color-muted text-600 text-uppercase"
						>{$format(`current`, {
							default: 'Current',
						})}</th
					>
					<th class="text-color-muted text-600 text-uppercase"
						>{$format(`label_voltage`, {
							default: 'Voltage',
						})}</th
					>
				</tr>
			</thead>
			<tbody>
				{#each ['a', 'b', 'c'] as phase}
					<tr>
						<th
							>{$format(`label_phase_${phase}`, {
								default: `phase ${phase}`,
							})}</th
						>
						<td>{statuses.find((i) => i.name == `${phase}_power`)?.value || 'N/A'}</td>
						<td>{statuses.find((i) => i.name == `${phase}_current`)?.value || 'N/A'}</td>
						<td>{statuses.find((i) => i.name == `${phase}_voltage`)?.value || 'N/A'}</td>
					</tr>
				{/each}
			</tbody>
		</PlainTable>
	</FlexibleCard>
{:else}
	<Alert
		type="info"
		class="mb-m"
		heading={$format('no_active_charging_session_heading', {
			default: 'No active charging session',
		})}
		message={$format('no_active_charging_session_message', {
			default: 'Live energy data will appear here once a charging session begins.',
		})}
	/>
{/if}
<hr class="mb-m" />
<Text class="text-bold mb-s" styleType="h2"
	>{$format('label_historic_data', {
		default: 'Historic data',
	})}</Text
>

<style>
	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
		gap: var(--spacing-s);
	}
	thead th {
		text-align: center;
	}
	tbody > tr > td {
		font-size: 12px;
	}
</style>
